import type { MaybeRefOrGetter } from 'vue';
import { computed, readonly, ref, toValue, watch } from 'vue';

export interface UseGridNavigationOptions {
	columns: MaybeRefOrGetter<number>;
	total: MaybeRefOrGetter<number>;
	wrapHorizontal?: MaybeRefOrGetter<boolean>;
	disabled?: MaybeRefOrGetter<boolean>;
	onActivate?: (event: KeyboardEvent) => void;
	onSelect?: (event: KeyboardEvent) => void;
}

type GridNavigationDirection = 'left' | 'right' | 'up' | 'down';

export function useGridNavigation(options: UseGridNavigationOptions) {
	const columns = computed(() => Math.max(1, Math.floor(toValue(options.columns))));
	const total = computed(() => Math.max(0, Math.floor(toValue(options.total))));
	const wrapHorizontal = computed(() => toValue(options.wrapHorizontal) ?? true);
	const isDisabled = computed(() => toValue(options.disabled) ?? false);

	const focusedIndex = ref(-1);
	const elements = new Map<number, HTMLElement>();

	// so the grid is a single tab stop.
	const tabbableIndex = computed(() => Math.max(focusedIndex.value, 0));

	watch(focusedIndex, (index) => {
		const element = elements.get(index);
		element?.focus();
		element?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
	});

	watch(total, (newTotal) => {
		if (focusedIndex.value >= newTotal) {
			focusedIndex.value = -1;
		}
	});

	function registerItem(index: number, element: HTMLElement | null) {
		if (element) {
			elements.set(index, element);
		}
		else {
			elements.delete(index);
		}
	}

	function setFocused(index: number) {
		if (index < 0 || index >= total.value) {
			focusedIndex.value = -1;
			return;
		}

		focusedIndex.value = index;
	}

	function handleArrowLeft(event: KeyboardEvent) {
		navigate(event, 'left');
	}

	function handleArrowRight(event: KeyboardEvent) {
		navigate(event, 'right');
	}

	function handleArrowUp(event: KeyboardEvent) {
		navigate(event, 'up');
	}

	function handleArrowDown(event: KeyboardEvent) {
		navigate(event, 'down');
	}

	function handleActivate(event: KeyboardEvent) {
		if (isDisabled.value) return;
		options.onActivate?.(event);
	}

	function handleSelect(event: KeyboardEvent) {
		if (isDisabled.value) return;
		options.onSelect?.(event);
	}

	function navigate(_event: KeyboardEvent, direction: GridNavigationDirection) {
		if (isDisabled.value || total.value === 0) return;

		const next = getNextIndex(direction);

		if (next === null) return;

		focusedIndex.value = next;
	}

	function getNextIndex(direction: GridNavigationDirection): number | null {
		const max = total.value - 1;
		const cols = columns.value;
		const current = focusedIndex.value;

		// Starting from no focus — first arrow press lands on item 0.
		if (current < 0) {
			return 0;
		}

		switch (direction) {
			case 'right': {
				const atRowEnd = (current + 1) % cols === 0;

				if (atRowEnd && !wrapHorizontal.value) return null;

				return current >= max ? null : current + 1;
			}
			case 'left': {
				const atRowStart = current % cols === 0;

				if (atRowStart && !wrapHorizontal.value) return null;

				return current <= 0 ? null : current - 1;
			}
			case 'down': {
				const target = current + cols;

				if (current >= max) return null;

				return Math.min(target, max);
			}
			case 'up': {
				const target = current - cols;

				return target < 0 ? null : target;
			}
		}
	}

	return {
		focusedIndex: readonly(focusedIndex),
		tabbableIndex,
		registerItem,
		setFocused,
		handleArrowLeft,
		handleArrowRight,
		handleArrowUp,
		handleArrowDown,
		handleActivate,
		handleSelect,
	};
}
